import { useMemo, type MouseEvent, type ReactNode } from 'react';
import {
  Ban, Castle, ChevronRight, CircleCheck, Clock, DatabaseZap, Globe, Grid3x3, Image as ImageIcon, ImageOff, Layers, Loader2,
  LocateOff, MapPin, MapPinOff, SquareDashed, TriangleAlert, Trees, Zap, ZapOff, type LucideIcon,
} from 'lucide-react';
import { useWorldsStore } from '../worlds.store';
import { fmtDate, fmtInt } from '../constants';
import { WORLDS_TABS, type GoToTab, type WorldsTabKey } from '../worlds.tabs';
import { COUNTRY_FILTERS, useWorldsStats, type CountryFilterKey, type WorldsStats } from '../useWorldsStats';
import { CardTitle, EmptyState, StatTile, StatusBadge, cx, toneClass, type Tone } from '../components/ui';
import { staticBackgroundFor } from '../map/staticBackgrounds';
import type { Country, RosterStatus } from '../worlds.types';
import ws from '../WorldsPage.module.scss';
import hs from './HomeTab.module.scss';

// ============================================================
// Onglet Accueil : vue d'ensemble de la page Mondes
// ============================================================
// Pendant du hub de Config Mondes (NGConfigAPI : cartes vers chaque sous-écran), avec en plus les
// chiffres clés du niveau affiché et une liste « À surveiller ». Tout vient du store (useWorldsStats) :
// aucun appel, aucune écriture, les chiffres suivent le poll. Identique en lecture seule, sauf les
// descriptions et le lien « Créer un niveau ».
//
// Navigation par l'URL uniquement (contrats de worlds.tabs.ts) : ?tab=pays&filtre=<clé> pour les
// lignes sur les pays (compte identique à la puce de l'onglet Pays, COUNTRY_FILTERS),
// ?tab=niveaux&nouveau=1 quand aucun niveau n'existe.
// ============================================================

/** Descriptions des cartes en lecture seule (les phrases de WORLDS_TABS décrivent l'édition). */
const READONLY_DESCRIPTIONS: Partial<Record<WorldsTabKey, string>> = {
  carte: 'Voir les chunks de chaque pays, leurs centres et les régions, tels que le jeu les sert.',
  pays: 'Consulter les pays : noms, couleurs, drapeaux, centres, activation et possession en jeu.',
  regions: 'Consulter les géo-régions, leurs pays et leurs stocks de pétrole et de gaz.',
  marqueurs: 'Voir les marqueurs de biomes placés sur la carte et le catalogue des biomes.',
};

const ROSTER_ICON: Record<RosterStatus, LucideIcon> = {
  disabled: ZapOff,
  idle: Zap,
  pending: Clock,
  ok: CircleCheck,
  error: TriangleAlert,
  blocked: Ban,
};

const TONE_ORDER: Record<Tone, number> = { bad: 0, warn: 1, info: 2, off: 3, ok: 4 };

/** « 1 pays », « 2 niveaux »... (pluriel donné quand il n'est pas régulier). */
function pl(n: number, one: string, many = `${one}s`): string {
  return `${fmtInt(n)} ${n > 1 ? many : one}`;
}

/** « A, B, C et 9 autres » : quelques exemples d'une liste. */
function sample(names: string[], max = 4): string {
  if (names.length <= max) {
    return names.length > 1 ? `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}` : names.join('');
  }
  const rest = names.length - max;
  return `${names.slice(0, max).join(', ')} et ${fmtInt(rest)} ${rest > 1 ? 'autres' : 'autre'}`;
}

/** Lien relatif vers un onglet (?tab=...) : href réel pour le clic milieu, onGo pour le clic simple. */
function hrefOf(tab: WorldsTabKey, extra?: Record<string, string>): string {
  return `?${new URLSearchParams({ tab, ...(extra ?? {}) }).toString()}`;
}

function plainClick(e: MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

interface WatchItem {
  key: string;
  tone: Tone;
  icon: LucideIcon;
  text: ReactNode;
  note?: ReactNode;
  tab: WorldsTabKey;
  extra?: Record<string, string>;
  /** Infobulle : où mène la ligne. */
  title: string;
}

export function HomeTab({ readOnly, onGo }: {
  readOnly: boolean;
  /** Navigation : onGo('pays', { filtre: 'sans-centre' }) mène à ?tab=pays&filtre=sans-centre. */
  onGo: GoToTab;
}) {
  const stats = useWorldsStats();
  const state = useWorldsStore((s) => s.state);
  const level = useWorldsStore((s) => s.level);
  const levels = useWorldsStore((s) => s.levels);
  const levelsVersion = useWorldsStore((s) => s.levelsVersion);
  const countries = useWorldsStore((s) => s.countries);
  const geoRegions = useWorldsStore((s) => s.geoRegions);
  const biomesCount = useWorldsStore((s) => s.biomes.length);

  const levelTitle = useMemo(() => {
    if (!level) return null;
    return levels.find((l) => l.name === level)?.title ?? state?.levels.find((l) => l.name === level)?.title ?? level;
  }, [level, levels, state]);
  const mainTitle = useMemo(() => {
    const main = state?.mainLevel;
    if (!main) return null;
    return levels.find((l) => l.name === main)?.title ?? main;
  }, [state, levels]);

  // Pays de chaque filtre, avec le même test que les puces de l'onglet Pays.
  const byFilter = useMemo(() => {
    const ctx = { founded: null };
    const m = new Map<CountryFilterKey, Country[]>();
    for (const f of COUNTRY_FILTERS) {
      if (f.needsPossession) continue;
      m.set(f.key, countries.filter((c) => f.test(c, ctx)));
    }
    return m;
  }, [countries]);
  const inFilter = (key: CountryFilterKey) => byFilter.get(key) ?? [];

  const watch = useMemo(
    () => buildWatch({ stats, readOnly, level, levelTitle, levelsLoaded: levelsVersion !== -1, levels, countries, geoRegions, byFilter }),
    [stats, readOnly, level, levelTitle, levelsVersion, levels, countries, geoRegions, byFilter],
  );

  const c = stats.countries;
  const r = stats.regions;
  const mk = stats.markers;
  const roster = stats.roster;
  const spinner = <Loader2 size={18} className={ws.spinning} aria-label="Chargement" />;
  const onLevel = levelTitle;
  const activeNoCenter = inFilter('sans-centre').filter((x) => x.enabled).length;

  // ---- Tuiles ----
  const RosterIcon = roster ? ROSTER_ICON[roster.status] ?? Zap : ZapOff;
  const tiles = (
    <div className={cx(ws.statGrid, hs.stats)}>
      <StatTile icon={Castle} label="Pays actifs" title="Ouvrir l'onglet Pays"
        tone={c.loaded && activeNoCenter > 0 ? 'warn' : undefined}
        value={c.loaded ? <>{fmtInt(c.enabled)}<span className={hs.valueTotal}> sur {fmtInt(c.total)}</span></> : spinner}
        sub={c.loaded && (
          <>
            {c.noCenter > 0
              ? <span className={ws.textWarn}>{fmtInt(c.noCenter)} sans f home</span>
              : c.total > 0 ? 'Tous ont un f home' : 'Aucun pays'}
            {c.founded != null && <> · {pl(c.founded, 'fondé')}</>}
          </>
        )}
        onClick={() => onGo('pays')} />

      <StatTile icon={Grid3x3} label="Chunks peints"
        title={level ? 'Ouvrir la carte' : 'Aucun niveau : ouvrir les niveaux'}
        value={c.loaded ? fmtInt(stats.chunks) : spinner}
        sub={level
          ? (c.loaded && <>{pl(c.withChunks, 'pays', 'pays')} avec des chunks · {onLevel}</>)
          : <span className={ws.textWarn}>Aucun niveau</span>}
        onClick={() => (level ? onGo('carte') : onGo('niveaux', readOnly ? undefined : { nouveau: '1' }))} />

      <StatTile icon={MapPin} label="Géo-régions" title="Ouvrir l'onglet Régions"
        tone={c.loaded && c.noRegion > 0 ? 'warn' : undefined}
        value={r.loaded ? fmtInt(r.total) : spinner}
        sub={c.loaded && r.loaded && (c.noRegion > 0
          ? <span className={ws.textWarn}>{pl(c.noRegion, 'pays', 'pays')} sans région</span>
          : c.total > 0 ? 'Tous les pays ont une région' : pl(r.empty, 'région vide', 'régions vides'))}
        onClick={() => onGo('regions')} />

      <StatTile icon={Trees} label="Marqueurs de biomes"
        title={level ? 'Ouvrir l\'onglet Marqueurs de biomes' : 'Aucun niveau : ouvrir les niveaux'}
        tone={mk.loaded && mk.orphans > 0 ? 'warn' : undefined}
        value={!level ? fmtInt(0) : mk.loaded ? fmtInt(mk.total) : spinner}
        sub={!level
          ? <span className={ws.textWarn}>Aucun niveau</span>
          : mk.loaded && (mk.orphans > 0
            ? <><span className={ws.textWarn}>{fmtInt(mk.orphans)} à nettoyer</span> · {onLevel}</>
            : <>Aucun à nettoyer · {onLevel}</>)}
        onClick={() => (level ? onGo('marqueurs') : onGo('niveaux', readOnly ? undefined : { nouveau: '1' }))} />

      <StatTile icon={RosterIcon} label="Roster en jeu" tone={roster?.tone ?? 'off'}
        title={roster?.lastError ?? 'Synchro du roster fondable (NGCountryAPI) : détails dans l\'onglet Pays'}
        value={<span className={hs.valueText}>{roster?.label ?? 'État inconnu'}</span>}
        sub={!roster
          ? 'Satellite sans état de synchro'
          : roster.lastSuccessAt
            ? <>Dernier succès : {fmtDate(roster.lastSuccessAt)}</>
            : roster.configured ? 'Aucun succès pour l\'instant' : 'NGCountryAPI non configurée'}
        onClick={() => onGo('pays')} />
    </div>
  );

  // ---- Cartes des onglets ----
  const hubKey = (key: WorldsTabKey): string | null => {
    switch (key) {
      case 'carte':
        if (!level) return 'Aucun niveau déclaré';
        return c.loaded ? `${pl(stats.chunks, 'chunk')} peint${stats.chunks > 1 ? 's' : ''} sur ${onLevel}` : null;
      case 'pays':
        return c.loaded ? `${pl(c.total, 'pays', 'pays')} · ${pl(c.enabled, 'actif')}` : null;
      case 'regions':
        return r.loaded ? `${pl(r.total, 'géo-région')} · ${pl(c.total - c.noRegion, 'pays affecté', 'pays affectés')}` : null;
      case 'niveaux':
        if (levelsVersion === -1) return null;
        return mainTitle ? `${pl(stats.levels, 'niveau', 'niveaux')} · principal : ${mainTitle}` : pl(stats.levels, 'niveau', 'niveaux');
      case 'marqueurs':
        if (!level) return 'Aucun niveau déclaré';
        return mk.loaded ? `${pl(mk.total, 'marqueur')} sur ${onLevel} · ${pl(biomesCount, 'biome')}` : null;
      default:
        return null;
    }
  };

  const hub = (
    <nav className={ws.hubGrid} aria-label="Onglets de la page Mondes">
      {WORLDS_TABS.filter((t) => t.key !== 'accueil').map((t) => {
        const Icon = t.icon;
        const key = hubKey(t.key);
        const description = (readOnly && READONLY_DESCRIPTIONS[t.key]) || t.description;
        return (
          <a key={t.key} href={hrefOf(t.key)} className={cx(ws.hubCard, hs.hubLink)}
            onClick={(e) => { if (plainClick(e)) { e.preventDefault(); onGo(t.key); } }}>
            <Icon size={112} className={ws.hubWatermark} aria-hidden />
            <span className={ws.hubIcon} aria-hidden><Icon size={22} /></span>
            <span className={ws.hubBody}>
              <span className={ws.hubTitle}>{t.label}</span>
              <span className={ws.hubText}>{description}</span>
              <span className={ws.hubKey}>{key ?? 'Chargement…'}</span>
            </span>
            <ChevronRight size={16} className={ws.hubChevron} aria-hidden />
          </a>
        );
      })}
    </nav>
  );

  // ---- À surveiller ----
  const loadingWatch = !c.loaded || !r.loaded;
  const alerts = watch.filter((w) => w.tone === 'bad' || w.tone === 'warn').length;
  const watchCard = (
    <section className={ws.card} aria-label="À surveiller">
      <CardTitle icon={TriangleAlert}
        actions={!loadingWatch && (alerts > 0
          ? <StatusBadge tone="warn">{pl(alerts, 'point', 'points')}</StatusBadge>
          : <StatusBadge tone="ok" icon={CircleCheck}>En ordre</StatusBadge>)}>
        À surveiller
      </CardTitle>
      {loadingWatch ? (
        <EmptyState compact spin icon={Loader2} title="Chargement des chiffres…" />
      ) : watch.length === 0 ? (
        <EmptyState compact icon={CircleCheck} title="Rien à signaler"
          text={`Pays, centres, régions, marqueurs et synchro du roster sont en ordre${onLevel ? ` sur ${onLevel}` : ''}.`} />
      ) : (
        <ul className={cx(ws.watchList, hs.watch)}>
          {watch.map((w) => {
            const Icon = w.icon;
            return (
              <li key={w.key} className={toneClass(w.tone)}>
                <a href={hrefOf(w.tab, w.extra)} title={w.title}
                  onClick={(e) => { if (plainClick(e)) { e.preventDefault(); onGo(w.tab, w.extra); } }}>
                  <Icon size={16} aria-hidden />
                  <span className={hs.watchText}>
                    <span className={hs.watchMain}>{w.text}</span>
                    {w.note && <span className={hs.watchNote}>{w.note}</span>}
                  </span>
                  <ChevronRight size={14} className={hs.watchGo} aria-hidden />
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );

  const lastImport = state?.lastImport ?? null;

  return (
    <div className={ws.tabBody}>
      {tiles}
      <div className={hs.layout}>
        {hub}
        {watchCard}
      </div>
      {lastImport && (
        <p className={hs.footNote}>
          <DatabaseZap size={12} aria-hidden />
          Dernier import NG Java le {fmtDate(lastImport.importedAt)} par {lastImport.importedBy}
          {lastImport.mode === 'replace' ? ' (remplacement des données)' : ' (première importation)'}.
        </p>
      )}
    </div>
  );
}

// ---- Liste « À surveiller » ----

function buildWatch({ stats, readOnly, level, levelTitle, levelsLoaded, levels, countries, geoRegions, byFilter }: {
  stats: WorldsStats;
  readOnly: boolean;
  level: string | null;
  levelTitle: string | null;
  levelsLoaded: boolean;
  levels: { name: string; title: string; tileUrl: string | null }[];
  countries: Country[];
  geoRegions: { id: string; name: string; assignedLevel: string | null }[];
  byFilter: Map<CountryFilterKey, Country[]>;
}): WatchItem[] {
  const items: WatchItem[] = [];
  const list = (key: CountryFilterKey) => byFilter.get(key) ?? [];
  const slugs = (cs: Country[]) => sample(cs.map((c) => c.slug));
  const toPays = (filtre: CountryFilterKey) => ({ tab: 'pays' as const, extra: { filtre }, title: 'Voir ces pays dans l\'onglet Pays' });

  if (levelsLoaded && !level) {
    items.push({
      key: 'no-level', tone: 'warn', icon: Globe,
      text: 'Aucun niveau déclaré',
      note: 'La carte et les marqueurs de biomes s\'affichent sur un niveau (monde Minecraft), par exemple overworld.',
      tab: 'niveaux', extra: readOnly ? undefined : { nouveau: '1' },
      title: readOnly ? 'Voir les niveaux' : 'Créer un niveau',
    });
  }

  const roster = stats.roster;
  if (roster && (roster.status === 'error' || roster.status === 'blocked')) {
    items.push({
      key: 'roster', tone: 'bad', icon: ROSTER_ICON[roster.status],
      text: roster.status === 'blocked' ? 'Synchro du roster bloquée' : 'Synchro du roster en échec',
      note: roster.lastError ?? (roster.status === 'blocked'
        ? 'Refus d\'un garde-fou de NGCountryAPI : action manuelle requise dans l\'onglet Pays.'
        : 'Détails et nouvelle tentative dans l\'onglet Pays.'),
      tab: 'pays', title: 'Voir la synchro dans l\'onglet Pays',
    });
  }

  if (stats.countries.loaded) {
    const noCenter = list('sans-centre');
    if (noCenter.length > 0) {
      const active = noCenter.filter((c) => c.enabled).length;
      items.push({
        key: 'sans-centre', tone: active > 0 ? 'warn' : 'off', icon: MapPinOff,
        text: <><strong>{pl(noCenter.length, 'pays', 'pays')}</strong> sans f home</>,
        note: `${active > 0
          ? `Dont ${pl(active, 'actif')}, exclu${active > 1 ? 's' : ''} du roster fondable tant qu'il${active > 1 ? 's n\'en ont' : ' n\'en a'} pas`
          : 'Tous inactifs : sans effet en jeu pour l\'instant'} : ${slugs(noCenter)}.`,
        ...toPays('sans-centre'),
      });
    }

    const outside = list('hors-territoire');
    if (outside.length > 0) {
      items.push({
        key: 'hors-territoire', tone: 'warn', icon: LocateOff,
        text: <><strong>{pl(outside.length, 'pays', 'pays')}</strong> avec un f home hors de {outside.length > 1 ? 'leur' : 'son'} territoire</>,
        note: `${levelTitle ? `Sur ${levelTitle} : ` : ''}${slugs(outside)}.`,
        ...toPays('hors-territoire'),
      });
    }

    if (level) {
      const noChunk = list('sans-chunk');
      if (noChunk.length > 0) {
        const active = noChunk.filter((c) => c.enabled).length;
        items.push({
          key: 'sans-chunk', tone: active > 0 ? 'warn' : 'off', icon: SquareDashed,
          text: <><strong>{pl(noChunk.length, 'pays', 'pays')}</strong> sans chunk sur {levelTitle}</>,
          note: `${active > 0 ? `Dont ${pl(active, 'actif')} : ` : 'Tous inactifs : '}${slugs(noChunk)}.`,
          ...toPays('sans-chunk'),
        });
      }
    }

    const noRegion = list('sans-region');
    if (noRegion.length > 0) {
      items.push({
        key: 'sans-region', tone: 'warn', icon: MapPin,
        text: <><strong>{pl(noRegion.length, 'pays', 'pays')}</strong> sans géo-région</>,
        note: `${slugs(noRegion)}.`,
        ...toPays('sans-region'),
      });
    }
  }

  if (stats.countries.loaded && stats.regions.loaded && stats.regions.idle > 0) {
    // Même règle que useWorldsStats : aucun pays et aucun niveau assigné.
    const withCountries = new Set(countries.map((c) => c.geoRegionId).filter(Boolean));
    const idle = geoRegions.filter((g) => !g.assignedLevel && !withCountries.has(g.id));
    items.push({
      key: 'regions-idle', tone: 'warn', icon: Layers,
      text: <><strong>{pl(stats.regions.idle, 'géo-région')}</strong> sans effet en jeu</>,
      note: `Ni pays ni niveau assigné : ${sample(idle.map((g) => g.name))}.`,
      tab: 'regions', title: 'Voir les géo-régions',
    });
  }

  if (level && stats.markers.loaded && stats.markers.orphans > 0) {
    items.push({
      key: 'markers', tone: 'warn', icon: Trees,
      text: <><strong>{pl(stats.markers.orphans, 'marqueur')}</strong> à nettoyer sur {levelTitle}</>,
      note: 'Biome absent ou désactivé : ces marqueurs seront ignorés par le jeu.',
      tab: 'marqueurs', title: 'Voir les marqueurs de biomes',
    });
  }

  if (levelsLoaded) {
    // Sans tileUrl : image fixe du terrain si le niveau en a une (staticBackgrounds), sinon
    // quadrillage neutre.
    const noTiles = levels.filter((l) => !l.tileUrl);
    if (noTiles.length > 0) {
      const bgs = noTiles.map((l) => staticBackgroundFor(l));
      const withImage = noTiles.filter((_, i) => bgs[i]);
      const neutral = noTiles.filter((_, i) => !bgs[i]);
      const origin = bgs.find((b) => b)?.origin;
      const multi = noTiles.length > 1;
      const titles = (ls: typeof noTiles) => (multi ? ` (${sample(ls.map((l) => l.title))})` : '');
      const shows = [
        withImage.length > 0 && `une image fixe du terrain ${origin} en attendant les tuiles${titles(withImage)}`,
        neutral.length > 0 && `un quadrillage neutre${titles(neutral)}`,
      ].filter(Boolean);
      items.push({
        key: 'tiles', tone: 'off', icon: neutral.length > 0 ? ImageOff : ImageIcon,
        text: !multi
          ? withImage.length > 0
            ? <>Image fixe en fond de carte sur {noTiles[0].title}</>
            : <>Fond de carte neutre sur {noTiles[0].title}</>
          : <><strong>{pl(noTiles.length, 'niveau', 'niveaux')}</strong> sans tuiles</>,
        note: `Aucune URL de tuiles : la carte affiche ${shows.join(' ; ')}. ${multi ? 'Les URL se renseignent' : 'L\'URL se renseigne'} dans Mondes (niveaux).`,
        tab: 'niveaux', title: 'Voir les niveaux',
      });
    }
  }

  return items.sort((a, b) => TONE_ORDER[a.tone] - TONE_ORDER[b.tone]);
}
